"use client";
import React, { useEffect, useState } from "react";
import HeroSection from "@/components/hero";
import Footer from "@/components/UI/footer/footer";
import Topbar from "@/components/UI/navbar/topbar";
import InfoCard from "@/components/app/rekrutacja/InfoCard";
import { motion } from "framer-motion";
import {
  AcademicCapIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  StarIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";
import recruitmentContent from "@/content/recruitment.json";
import {SEOUtil} from "@/utils/SEOUtil";

type TimelineKey =
  | "applications"
  | "tests"
  | "documents"
  | "preliminary"
  | "confirmation"
  | "final";

type RecruitmentMode = "regular" | "supplementary";

type TimelineStepDetail = {
  label: string;
  value: string;
};

type TimelineStep = {
  title: string;
  date?: string;
  icon: React.ReactNode;
  details?: TimelineStepDetail[];
  badge?: string;
  hint?: string;
};

type InfoCardContentText = {
  type: "text";
  value: string;
};

type InfoCardContentLink = {
  type: "link";
  prefix?: string;
  label: string;
  href: string;
  suffix?: string;
  target?: string;
  rel?: string;
};

type InfoCardContentEntry = InfoCardContentText | InfoCardContentLink;

type IconName =
  | "AcademicCapIcon"
  | "ArrowPathIcon"
  | "CalendarDaysIcon"
  | "CheckCircleIcon"
  | "ClipboardDocumentCheckIcon"
  | "DocumentTextIcon"
  | "StarIcon"
  | "TrophyIcon";

type RawInfoCard = {
  title: string;
  icon: IconName;
  content: InfoCardContentEntry[];
};

type TimelineConfigEntry = {
  key: TimelineKey;
  title: string;
  Icon: typeof DocumentTextIcon;
  iconClassName: string;
  badge?: string;
  hint?: string;
};

type RecruitmentRegion = {
  id: string;
  label: string;
  timelines: Partial<
    Record<RecruitmentMode, Partial<Record<TimelineKey, string>>>
  >;
};

type RecruitmentModeOption = {
  id: RecruitmentMode;
  label: string;
  description?: string;
};

type RawCommonTimelineStep = {
  title: string;
  date?: string;
  icon: IconName;
  details?: TimelineStepDetail[];
};

type RecruitmentContent = {
  defaultRegion?: string;
  defaultMode?: RecruitmentMode;
  modeOptions?: RecruitmentModeOption[];
  commonTimelineSteps?: RawCommonTimelineStep[];
  regions: RecruitmentRegion[];
  infoCards: RawInfoCard[];
};

const ICON_MAP = {
  AcademicCapIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  StarIcon,
  TrophyIcon,
} as const;

const TIMELINE_BASE_CONFIG: TimelineConfigEntry[] = [
  {
    key: "applications",
    title: "Składanie wniosków",
    Icon: DocumentTextIcon,
    iconClassName: "w-10 h-10 text-btn",
  },
  {
    key: "tests",
    title:
      "Weź udział \n w sprawdzianach językowych lub próbach sprawności fizycznej",
    Icon: AcademicCapIcon,
    iconClassName: "w-10 h-10 text-btn",
    badge: "Zależy od szkoły",
    hint: "Ten etap nie zawsze jest obowiązkowy.",
  },
  {
    key: "documents",
    title: "Dostarczanie świadectw\ni wyników egzaminów",
    Icon: CalendarDaysIcon,
    iconClassName: "w-10 h-10 text-btn",
  },
  {
    key: "preliminary",
    title: "Wyniki wstępne",
    Icon: AcademicCapIcon,
    iconClassName: "w-10 h-10 text-btn",
  },
  {
    key: "confirmation",
    title: "Potwierdzanie wyboru",
    Icon: CheckCircleIcon,
    iconClassName: "w-10 h-10 text-btn",
  },
  {
    key: "final",
    title: "Wyniki ostateczne",
    Icon: TrophyIcon,
    iconClassName: "w-10 h-10 text-btn",
  },
];

const buildTimeline = (dates: Record<TimelineKey, string>): TimelineStep[] =>
  TIMELINE_BASE_CONFIG.map(({ key, title, Icon, iconClassName, badge, hint }) => ({
    title,
    date: dates[key],
    icon: <Icon className={iconClassName} />,
    badge,
    hint,
  }));

const rawRecruitmentContent = recruitmentContent as RecruitmentContent;

const REGIONS_DATA = rawRecruitmentContent.regions;

const MODE_OPTIONS: RecruitmentModeOption[] =
  rawRecruitmentContent.modeOptions ?? [
    {
      id: "regular",
      label: "Rekrutacja zwykła",
      description: "Pierwszy etap naboru do szkół",
    },
    {
      id: "supplementary",
      label: "Rekrutacja uzupełniająca",
      description: "Drugi etap dla wolnych miejsc",
    },
  ];

const MODE_ICON_MAP: Record<RecruitmentMode, typeof DocumentTextIcon> = {
  regular: AcademicCapIcon,
  supplementary: ArrowPathIcon,
};

const REGION_MAP = REGIONS_DATA.reduce<Record<string, RecruitmentRegion>>(
  (acc, region) => {
    acc[region.id] = region;
    return acc;
  },
  {},
);

const DEFAULT_REGION =
  rawRecruitmentContent.defaultRegion ?? REGIONS_DATA[0]?.id ?? "";

const FALLBACK_TIMELINE_DATES: Record<TimelineKey, string> = {
  applications: "Brak danych",
  tests: "Brak danych",
  documents: "Brak danych",
  preliminary: "Brak danych",
  confirmation: "Brak danych",
  final: "Brak danych",
};

const COMMON_TIMELINE_STEPS_RAW =
  rawRecruitmentContent.commonTimelineSteps ?? [];

const COMMON_TIMELINE_STEPS: TimelineStep[] = COMMON_TIMELINE_STEPS_RAW.map(
  (step) => {
    const IconComponent = ICON_MAP[step.icon] ?? DocumentTextIcon;

    return {
      title: step.title,
      date: step.date,
      icon: <IconComponent className="w-10 h-10 text-btn" />,
      details: step.details,
    };
  },
);

const buildTimelineForSelection = (regionId: string, mode: RecruitmentMode) => {
  const regionTimeline = REGION_MAP[regionId];
  const modeTimeline = regionTimeline?.timelines?.[mode] ?? {};

  const timeline = buildTimeline({
    ...FALLBACK_TIMELINE_DATES,
    ...modeTimeline,
  } as Record<TimelineKey, string>);

  timeline.splice(2, 0, ...COMMON_TIMELINE_STEPS);

  return timeline;
};

const mapContentEntry = (entry: InfoCardContentEntry): React.ReactNode => {
  if (entry.type === "text") {
    return entry.value;
  }

  return (
    <span>
      {entry.prefix ?? ""}
      <a
        href={entry.href}
        className="text-btn underline font-semibold"
        target={entry.target ?? undefined}
        rel={entry.rel ?? undefined}
      >
        {entry.label}
      </a>
      {entry.suffix ?? ""}
    </span>
  );
};

const renderTimelineDetailValue = (detail: TimelineStepDetail): React.ReactNode => {
  const shouldLinkZiu =
    detail.label === "Miejsce opublikowania wyników" &&
    detail.value.includes("ZIU");

  if (!shouldLinkZiu) {
    return detail.value;
  }

  const [beforeZiu, afterZiu] = detail.value.split("ZIU");

  return (
    <>
      {beforeZiu}
      <a
        href="https://ziu.gov.pl"
        target="_blank"
        rel="noopener noreferrer"
        className="text-btn underline font-semibold"
      >
        ZIU
      </a>
      {afterZiu ?? ""}
    </>
  );
};

const INFO_CARDS_DATA = rawRecruitmentContent.infoCards.map((card) => {
  const IconComponent = ICON_MAP[card.icon] ?? DocumentTextIcon;

  return {
    title: card.title,
    icon: <IconComponent className="w-8 h-8" />,
    content: card.content.map(mapContentEntry),
  };
});

const INITIAL_REGION = DEFAULT_REGION || REGIONS_DATA[0]?.id || "";
const DEFAULT_MODE: RecruitmentMode =
  rawRecruitmentContent.defaultMode ?? MODE_OPTIONS[0]?.id ?? "regular";

const RecrutationPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string>(INITIAL_REGION);
  const [selectedMode, setSelectedMode] =
    useState<RecruitmentMode>(DEFAULT_MODE);
  const [timelineSteps, setTimelineSteps] = useState<TimelineStep[]>(
    buildTimelineForSelection(INITIAL_REGION, DEFAULT_MODE),
  );

  useEffect(() => {
    setTimelineSteps(buildTimelineForSelection(selectedRegion, selectedMode));
  }, [selectedRegion, selectedMode]);

  const selectedModeLabel =
    MODE_OPTIONS.find((mode) => mode.id === selectedMode)?.label ??
    "Rekrutacja";

  return (
    <>
      <SEOUtil name="Rekrutacja" url="/app/rekrutacja" description="Sprawdzaj terminy rekrutacji do szkół średnich, etapy naboru i kluczowe daty w Twoim regionie." mainEntityOfPage page />
      <Topbar />
      <main className="text-gray-900">
        <HeroSection
          src="/rekrutacja/rekrutacja.webp"
          heading="Informacje o rekrutacji"
          other={{
            italic: "do szkół średnich",
            text: "Znajdziesz tutaj wszystkie informacje, których potrzebujesz!",
            href: "#wybor-wojewodztwa",
          }}
          className='whitespace-pre-line sm:whitespace-normal pt-6 md:pt-10 [&>div]:mr-0 [&>div]:w-full [&>div]:flex [&>div]:justify-center [&>div>div]:w-[min(88vw,980px)] [&>div>div]:px-[clamp(0.8rem,2vw,2rem)] [&_p]:max-w-[32ch] [&_p]:leading-[1.42] max-sm:[&_p]:max-w-full'
        />

        <motion.section
          id="wybor-wojewodztwa"
          className="py-16 md:py-20 px-4 sm:px-6 bg-main text-white16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          viewport={{ once: true }}
        >
          <div className="max-w-4xl mx-auto flex flex-col gap-6">
            <motion.h2
              className="text-3xl md:text-5xl font-extrabold text-center"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 }}
              viewport={{ once: true }}
            >
              Wybierz region i etap rekrutacji
            </motion.h2>
            <motion.p
              className="text-base md:text-lg text-center text-white48 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.12 }}
              viewport={{ once: true }}
            >
              Harmonogram dopasowany do regionu i trybu naboru.
            </motion.p>
            <motion.div
              className="flex flex-col items-stretch gap-4 bg-white/5 border border-white/15 rounded-2xl p-4 md:p-6"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="relative flex-1 w-full">
                <select
                  id="region-select"
                  value={selectedRegion}
                  onChange={(event) => setSelectedRegion(event.target.value)}
                  className="w-full appearance-none px-5 py-3 bg-white text-main rounded-xl border border-white/20 focus:outline-none focus:ring-2 focus:ring-btn/50 text-base md:text-lg font-semibold"
                >
                  {REGIONS_DATA.map((region) => (
                    <option
                      key={region.id}
                      value={region.id}
                      className="text-gray-900"
                    >
                      {region.label}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute inset-y-0 right-5 flex items-center text-main">
                  <ChevronDownIcon className="w-5 h-5" />
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {MODE_OPTIONS.map((mode) => {
                  const isActive = selectedMode === mode.id;
                  const ModeIcon = MODE_ICON_MAP[mode.id] ?? DocumentTextIcon;

                  return (
                    <button
                      key={mode.id}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => setSelectedMode(mode.id)}
                      className={`text-left rounded-xl border px-4 py-3 transition-colors ${
                        isActive
                          ? "bg-white text-main border-white/70 shadow-[0_8px_22px_rgba(0,0,0,0.2)]"
                          : "bg-white/5 text-white16 border-white/20 hover:bg-white/10"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`inline-flex shrink-0 items-center justify-center rounded-lg p-2 ${
                            isActive
                              ? "bg-main/10 text-main"
                              : "bg-white/10 text-white16"
                          }`}
                        >
                          <ModeIcon className="w-5 h-5" />
                        </span>
                        <span>
                          <span className="block text-base md:text-lg font-semibold">
                            {mode.label}
                          </span>
                          {mode.description ? (
                            <span
                              className={`block text-sm mt-1 ${
                                isActive ? "text-subheading" : "text-white48"
                              }`}
                            >
                              {mode.description}
                            </span>
                          ) : null}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </motion.section>

        <section
          id="harmonogram"
          className="py-20 md:py-36 px-4 sm:px-6 bg-white5 scroll-mt-16"
        >
          <div className="max-w-6xl mx-auto">
            <motion.h2
              className="text-4xl md:text-6xl font-extrabold mb-10 md:mb-20 text-center text-main pb-2 md:pb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              Harmonogram rekrutacji
            </motion.h2>
            <p className="text-center text-base md:text-xl text-subheading mb-10 md:-mt-14 md:mb-16">
              Aktualnie wybrany tryb:{" "}
              <span className="font-semibold text-btn">
                {selectedModeLabel}
              </span>
            </p>

            <div className="relative pl-1 md:pl-0">
              <div className="absolute left-5 md:left-1/2 top-0 bottom-0 w-1 bg-btn rounded-full transform md:-translate-x-1/2"></div>

              <motion.ul
                className="space-y-8 md:space-y-20"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={{
                  hidden: {},
                  visible: { transition: { staggerChildren: 0.25 } },
                }}
              >
                {timelineSteps.map((step, i) => (
                  <motion.li
                    key={step.title}
                    className={`relative flex items-center justify-start ${
                      i % 2 === 0 ? "md:justify-start" : "md:justify-end"
                    }`}
                    initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <div className="relative overflow-hidden ml-12 md:ml-0 w-[calc(100%-3rem)] md:w-[45%] rounded-2xl md:rounded-3xl bg-white border border-main/20 p-5 sm:p-6 md:p-10 shadow-lg transition hover:-translate-y-1 md:hover:-translate-y-2 hover:shadow-2xl text-center">
                      {step.badge ? (
                        <span className="pointer-events-none absolute -left-20 top-8 z-10 inline-flex h-8 w-[22rem] -rotate-45 items-center justify-center bg-linear-to-r from-main to-btn text-xs font-semibold tracking-wide text-white16 whitespace-nowrap shadow-md">
                          <span className="block w-full -translate-x-3 px-4 text-center md:-translate-x-5">
                            {step.badge}
                          </span>
                        </span>
                      ) : null}
                      <div className="flex flex-col items-center gap-3 md:gap-4 mb-3 md:mb-4">
                        <span className="inline-flex items-center justify-center rounded-xl p-2 bg-main/5 border border-main/10">
                          {step.icon}
                        </span>
                        <h3 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-heading whitespace-pre-line leading-tight text-center">
                          {step.title}
                        </h3>
                      </div>
                      {step.hint ? (
                        <p className="mb-3 text-sm md:text-base font-medium text-subheading text-center">
                          {step.hint}
                        </p>
                      ) : null}
                      {step.date ? (
                        <p className="mt-2 inline-flex w-full md:w-auto justify-center rounded-xl border border-btn/30 bg-btn/12 px-4 md:px-5 py-2 text-base md:text-xl font-semibold text-btn whitespace-pre-line text-center">
                          {step.date}
                        </p>
                      ) : null}
                      {step.details ? (
                        <div className="mt-4 md:mt-5 mx-auto w-full max-w-lg space-y-3 text-left">
                          {step.details.map((detail) => (
                            <div
                              key={detail.label}
                              className="rounded-xl md:rounded-2xl border border-btn/30 bg-btn/12 px-4 py-3"
                            >
                              <p className="text-xs font-semibold uppercase tracking-wide text-main">
                                {detail.label}
                              </p>
                              <p className="mt-1 text-sm sm:text-base md:text-lg text-subheading leading-relaxed">
                                {renderTimelineDetailValue(detail)}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <span className="absolute left-5 md:left-1/2 top-7 md:top-1/2 w-11 h-11 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-btn text-white font-extrabold text-base md:text-xl shadow-lg ring-4 ring-white/80 transform -translate-x-1/2 md:-translate-y-1/2 z-10">
                      {i + 1}
                    </span>
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </div>
        </section>

        <section id="zasady" className="py-28 px-6 bg-main">
          <div className="max-w-6xl mx-auto">
            <motion.h2
              className="text-4xl md:text-6xl font-extrabold mb-14 md:mb-20 text-center text-white16"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              Ważne informacje dla Ciebie!
            </motion.h2>

            <div className="columns-1 md:columns-2 gap-8 md:gap-12 space-y-12">
              {INFO_CARDS_DATA.map((item, i) => (
                <div key={item.title} className="break-inside-avoid">
                  <InfoCard
                    title={item.title}
                    icon={item.icon}
                    content={item.content}
                    isOpen={openIndex === i}
                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                    delay={i * 0.2}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
};

export default RecrutationPage;
